import React, { useEffect, useState } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import { Hotel } from '../types/hotel';
import { apiService } from '../services/api';
import Loader from '../components/Loader';
import ApprovalModal from '../components/ApprovalModal';
import {
  CheckCircle,
  MapPin,
  Phone,
  Mail,
  Clock,
  AlertCircle,
  Hotel as HotelIcon,
  ChevronRight,
} from 'lucide-react';

const PendingListings: React.FC = () => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [modalType, setModalType] = useState<'approve' | 'reject' | null>(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  useEffect(() => {
    fetchPendingHotels();
  }, []);

  const fetchPendingHotels = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getHotels();
      const pending = response.data.filter((h: any) => h.verificationStatus === 'pending');
      setHotels(pending);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch pending listings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (reason?: string) => {
    if (!selectedHotel) return;
    setIsProcessing(true);
    try {
      setModalType(null);
      setSelectedHotel(null);
      await fetchPendingHotels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve hotel');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (reason?: string) => {
    if (!selectedHotel || !reason) return;
    setIsProcessing(true);
    try {
      setModalType(null);
      setSelectedHotel(null);
      await fetchPendingHotels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject hotel');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-serif font-bold text-sage-900">Pending Review</h1>
          <p className="text-sm text-sage-500 mt-1">
            Review and approve or reject hotel listings submitted by owners
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-beige-300 p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-warm-500" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sage-500 font-medium">Total Pending</p>
                <p className="text-3xl font-bold text-sage-900 mt-1">{hotels.length}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-warm-100">
                <Clock size={18} className="text-warm-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-beige-300 p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-brand-500" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sage-500 font-medium">In Review</p>
                <p className="text-3xl font-bold text-sage-900 mt-1">{selectedHotel ? 1 : 0}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-brand-50">
                <HotelIcon size={18} className="text-brand-500" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-beige-300 p-5 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-0.5 bg-sage-400" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sage-500 font-medium">Waiting Queue</p>
                <p className="text-3xl font-bold text-sage-900 mt-1">
                  {Math.max(0, hotels.length - (selectedHotel ? 1 : 0))}
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-beige-100">
                <Clock size={18} className="text-sage-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Main */}
        {isLoading ? (
          <Loader fullScreen />
        ) : hotels.length === 0 ? (
          <div className="bg-white rounded-xl border border-beige-300 p-12 text-center">
            <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={24} className="text-green-500" />
            </div>
            <p className="text-sage-900 text-base font-semibold mb-1">All Caught Up</p>
            <p className="text-sage-500 text-sm">
              There are no pending listings. All hotel submissions have been reviewed.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sidebar List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl border border-beige-300 overflow-hidden sticky top-4">
                <div className="px-5 py-4 border-b border-beige-200">
                  <h2 className="text-sm font-semibold text-sage-900">Pending Hotels</h2>
                  <p className="text-xs text-sage-500 mt-0.5">{hotels.length} awaiting review</p>
                </div>
                <div className="divide-y divide-beige-200 max-h-[500px] overflow-y-auto">
                  {hotels.map((hotel) => (
                    <button
                      key={hotel.id}
                      onClick={() => { setSelectedHotel(hotel); setSelectedPhotoIndex(0); }}
                      className={`w-full text-left px-5 py-3.5 flex items-center gap-3 transition-colors ${
                        selectedHotel?.id === hotel.id
                          ? 'bg-brand-50 border-l-2 border-brand-500'
                          : 'hover:bg-beige-50 border-l-2 border-transparent'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-sage-900 truncate">{hotel.propName}</p>
                        <p className="text-xs text-sage-500 mt-0.5">{hotel.ownerName}</p>
                        <div className="flex items-center gap-1 text-xs text-sage-400 mt-0.5">
                          <MapPin size={10} />
                          <span>{hotel.city}, {hotel.country}</span>
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-beige-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Detail View */}
            <div className="lg:col-span-2">
              {selectedHotel ? (
                <div className="space-y-5">
                  {/* Photo Gallery */}
                  {selectedHotel.photos && selectedHotel.photos.length > 0 && (
                    <div>
                      <div className="w-full rounded-xl overflow-hidden">
                        <img
                          src={selectedHotel.photos[selectedPhotoIndex].photo_url}
                          alt={selectedHotel.propName}
                          className="w-full h-56 object-cover"
                        />
                      </div>
                      <div className="flex gap-2 mt-2 overflow-x-auto">
                        {selectedHotel.photos.map((p, idx) => (
                          <button
                            key={p.id + idx}
                            onClick={() => setSelectedPhotoIndex(idx)}
                            className={`flex-shrink-0 rounded-lg overflow-hidden w-16 h-10 border-2 transition-colors ${
                              selectedPhotoIndex === idx ? 'border-brand-500' : 'border-beige-300'
                            }`}
                          >
                            <img src={p.photo_url} alt={`${selectedHotel.propName}-${idx}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Property Header */}
                  <div className="bg-white rounded-xl border border-beige-300 p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <h2 className="text-xl font-serif font-bold text-sage-900">{selectedHotel.propName}</h2>
                        <div className="flex items-center gap-1.5 text-sm text-sage-500 mt-1">
                          <MapPin size={14} className="text-sage-400" />
                          <span>{selectedHotel.address}, {selectedHotel.city}</span>
                        </div>
                      </div>
                      {(selectedHotel as any).basePrice && (
                        <div className="text-right flex-shrink-0">
                          <p className="text-2xl font-bold text-sage-900">${(selectedHotel as any).basePrice}</p>
                          <p className="text-xs text-sage-500">per night</p>
                        </div>
                      )}
                    </div>

                    {selectedHotel.description && (
                      <p className="text-sm text-sage-600 leading-relaxed mb-4">{selectedHotel.description}</p>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-sage-400 uppercase tracking-wider mb-1">Region</p>
                        <p className="text-sm font-medium text-sage-900">{selectedHotel.region}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-sage-400 uppercase tracking-wider mb-1">District</p>
                        <p className="text-sm font-medium text-sage-900">{selectedHotel.district}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-sage-400 uppercase tracking-wider mb-1">Type</p>
                        <span className="inline-block text-xs font-medium text-sage-600 bg-beige-100 px-2 py-1 rounded-md capitalize">
                          {selectedHotel.accommodationType?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-sage-400 uppercase tracking-wider mb-1">Rooms</p>
                        <p className="text-sm font-medium text-sage-900">
                          {selectedHotel.totalRooms} total, {selectedHotel.availableRooms} available
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Amenities */}
                  {/* {selectedHotel.amenities && selectedHotel.amenities.length > 0 && (
                    <div className="bg-white rounded-xl border border-beige-300 p-6">
                      <h3 className="text-sm font-semibold text-sage-900 mb-3">Amenities</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedHotel.amenities.map((amenity, idx) => (
                          <span
                            key={idx}
                            className="px-2.5 py-1 bg-brand-50 text-brand-600 border border-brand-200 rounded-lg text-xs font-medium"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )} */}

                  {/* Owner Info */}
                  <div className="bg-white rounded-xl border border-beige-300 p-6">
                    <h3 className="text-sm font-semibold text-sage-900 mb-4">Owner Information</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-sage-400 font-medium">Name</p>
                        <p className="text-sm text-sage-900 font-medium mt-0.5">{selectedHotel.ownerName}</p>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-sage-600">
                        <Phone size={14} className="text-sage-400" />
                        <span>{selectedHotel.ownerPhone}</span>
                      </div>
                      {selectedHotel.ownerEmail && (
                        <div className="flex items-center gap-2 text-sm text-sage-600">
                          <Mail size={14} className="text-sage-400" />
                          <span className="break-all">{selectedHotel.ownerEmail}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setModalType('approve')}
                      disabled={isProcessing}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-semibold text-sm rounded-xl transition-colors"
                    >
                      <CheckCircle size={16} />
                      Approve Listing
                    </button>
                    <button
                      onClick={() => setModalType('reject')}
                      disabled={isProcessing}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-semibold text-sm rounded-xl transition-colors"
                    >
                      Reject Listing
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-beige-300 p-12 text-center">
                  <div className="w-16 h-16 bg-beige-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <HotelIcon size={24} className="text-sage-400" />
                  </div>
                  <p className="text-sage-900 text-base font-semibold mb-1">Select a listing</p>
                  <p className="text-sage-500 text-sm">
                    Choose a hotel from the pending list to review its details
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Approval Modal */}
        {selectedHotel && (
          <ApprovalModal
            isOpen={modalType !== null}
            hotelName={selectedHotel.propName}
            type={modalType || 'approve'}
            onConfirm={modalType === 'approve' ? handleApprove : handleReject}
            onCancel={() => setModalType(null)}
            isLoading={isProcessing}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default PendingListings;
